import { de, enGB } from 'date-fns/locale';
import { endOfWeek, format, formatDistance, isValid, parse, parseISO, startOfWeek } from 'date-fns';

export const WEEK_STARTS_ON = 1; // Monday

type LocaleName = 'en' | 'de' | string

export const getDateFnsLocale = (lang: LocaleName) => {
  switch (lang) {
    case 'en':
    default:
      return enGB;
    case 'de':
      return de;
  }
};

/**
 * Wrapper around `format`. You can specify the locale, otherwise 'en' will be used by default.
 * @param date Date to format
 * @param formatStr Format that should be applied to the string, defaults to 'yyyy-MM-dd'
 * @param localeName Locale, defaults to 'en'
 */
export function formatDate(date: Date, formatStr: string = 'yyyy-MM-dd', localeName: LocaleName = 'en'): string {
  if (!isValidDate(date)) {
    return null;
  }
  return format(date, formatStr, { locale: getDateFnsLocale(localeName) });
}

/**
 * Wrapper around `format`. Will take the current language (from localStorage) as locale.
 * @param date Date to format
 * @param formatStr Format that should be applied to the string. Defaults to 'PP', the long localized format.  -- https://date-fns.org/v2.28.0/docs/format
 */
export function formatLocalizedDate(date: Date, formatStr: string = 'PP'): string {
  if (!isValidDate(date)) {
    return '';
  }

  let localeName = (window as any).localStorage.getItem('LANGUAGE');

  if (localeName?.includes('"')) {
    // the locale parsed from localstorage tends to contain quotes 2 times
    localeName = JSON.parse(localeName);
  }

  try {
    return formatDate(date, formatStr, localeName);
  } catch (err) {
    console.warn('Failed to \'formatLocalizedDate\'', { err });
  }
}

export function isValidDate(date: Date): boolean {
  return isValid(date);
}

export function isValidDateRange(fromDate: Date, toDate: Date) {
  return isValidDate(fromDate) && isValidDate(toDate);
}

/**
 * Pretty-prints a given date range and delimits the two boundaries
 * @param from Start date of the daterange.
 * @param to End date of the daterange.
 * @param formatStr Format that should be applied to the string.
 * @param localeName Locale, defaults to 'en'
 * @param separator Separator that should be displayed between the start and end date.
 */
export function formatDateRange(from: Date, to: Date, formatStr: string, localeName: LocaleName = 'en', separator: string = ' - ') {
  if (!isValidDateRange(from, to)) {
    return '';
  }
  const formattedFrom = formatDate(from, formatStr, localeName);
  const formattedTo = formatDate(to, formatStr, localeName);
  return `${formattedFrom}${separator}${formattedTo}`;
}

/**
 * Parses a formatted string into a Date.
 * @param dateAsString e.g. '2022-01-01'
 * @param formatOfDateString Format of the date-string ('yyyy-MM-dd' by default)
 */
export function parseDateString(dateAsString: string, formatOfDateString: string = 'yyyy-MM-dd'): Date {
  if (!dateAsString) return;
  const parsedDate = parse(dateAsString, formatOfDateString, new Date());
  return isValidDate(parsedDate) ? parsedDate : null;
}

/**
 * Parses a ISO date string into a Date.
 * @param isoDateString
 * @param options
 */
export function parseISODateString(isoDateString: string, options: {additionalDigits?: 0 | 1 | 2} = {}): Date {
  if (!isoDateString) return;
  const parsedDate = parseISO(isoDateString, options);
  return isValidDate(parsedDate) ? parsedDate : null;
}

/**
 * There's no global setting for `weekStartsOn` but we like Mondays.
 * This function should be used instead of the original `startOfWeek`
 * @param args
 */
export function europeanStartOfWeek(...args: Parameters<typeof startOfWeek>) {
  return startOfWeek(args[0], {
    ...args[1],
    weekStartsOn: WEEK_STARTS_ON,
  });
}

/**
 * There's no global setting for `weekStartsOn` but we like Mondays.
 * This function should be used instead of the original `endOfWeek`
 * @param args
 */
export function europeanEndOfWeek(...args: Parameters<typeof endOfWeek>) {
  return endOfWeek(args[0], {
    ...args[1],
    weekStartsOn: WEEK_STARTS_ON,
  });
}

/**
 * Returns relative time from now in words.
 * @param date
 */
export function getTimeFromNow(date: Date) {
  return formatDistance(date, new Date(), { addSuffix: true });
}
